export type SitemapRoute = {
  /**
   * Route path beginning with a slash. Dynamic segments (e.g. `:slug`) are excluded
   * from the sitemap in favour of CMS-driven feeds.
   */
  path: string;
  /**
   * Relative priority value in the sitemap. Defaults to `0.5` when omitted.
   */
  priority?: number;
  /**
   * How frequently the resource is expected to change. Defaults to `weekly`.
   */
  changefreq?: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
};

const trimTrailingSlash = (value: string) =>
  value !== "/" ? value.replace(/\/+$/, "") : value;

const ensureLeadingSlash = (value: string) =>
  value.startsWith("/") ? value : `/${value}`;

export type SiteMetadata = {
  siteUrl: string;
  siteName: string;
  defaultTitle: string;
  titleTemplate: string;
  defaultDescription: string;
  defaultSocialImage: string;
  twitterHandle: string;
  trailingSlash: boolean;
  sitemapDefaults: {
    changefreq: "always" | "hourly" | "daily" | "weekly" | "monthly" | "yearly" | "never";
    priority: number;
  };
};

export const siteMetadata: SiteMetadata = {
  /**
   * Canonical production origin for the public site.
   */
  siteUrl: "https://www.eruptionradio.uk",
  siteName: "Eruption Radio",
  defaultTitle: "Eruption Radio | Underground Music Station",
  titleTemplate: "%s | Eruption Radio",
  defaultDescription:
    "Stream underground house, jungle, drum & bass, and old skool classics 24/7 with Eruption Radio. Catch live shows, replays, and exclusive events from our London collective.",
  defaultSocialImage: "https://www.eruptionradio.uk/site/wp-content/uploads/2024/01/logomain.png",
  twitterHandle: "@eruptionradio",
  /**
   * SPA routes are served without trailing slashes, aside from the root path.
   */
  trailingSlash: false,
  /**
   * Default sitemap options when a route-specific value is not provided.
   */
  sitemapDefaults: {
    changefreq: "weekly" as const,
    priority: 0.5,
  },
};

export const sitemapRoutes: SitemapRoute[] = [
  { path: "/", changefreq: "daily", priority: 1.0 },
  { path: "/schedule", changefreq: "daily", priority: 0.9 },
  { path: "/shows", changefreq: "daily", priority: 0.9 },
  { path: "/presenters", changefreq: "weekly", priority: 0.7 },
  { path: "/about", changefreq: "monthly" },
  { path: "/blog", changefreq: "daily", priority: 0.8 },
  { path: "/events", changefreq: "daily", priority: 0.8 },
  { path: "/photos", changefreq: "weekly", priority: 0.7 },
  { path: "/store", changefreq: "daily", priority: 0.8 },
  { path: "/contact", changefreq: "weekly", priority: 0.6 },
  { path: "/advertise", changefreq: "monthly", priority: 0.6 },
  { path: "/privacy", changefreq: "yearly", priority: 0.4 },
  { path: "/terms", changefreq: "yearly", priority: 0.4 },
  { path: "/legal", changefreq: "yearly", priority: 0.4 },
  { path: "/maintenance", changefreq: "yearly", priority: 0.2 },
  { path: "/500", changefreq: "yearly", priority: 0.2 },
  { path: "/order-confirmation", changefreq: "monthly", priority: 0.5 },
];

/**
 * Build an absolute canonical URL using the configured site metadata while
 * enforcing the trailing slash policy.
 */
export const buildCanonicalUrl = (path: string) => {
  const normalizedPath = trimTrailingSlash(ensureLeadingSlash(path));

  if (normalizedPath === "/") {
    return `${siteMetadata.siteUrl}/`;
  }

  return siteMetadata.trailingSlash
    ? `${siteMetadata.siteUrl}${normalizedPath}/`
    : `${siteMetadata.siteUrl}${normalizedPath}`;
};
